import { FC, useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
  Link,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  getColorandFinish,
  getImages,
  getSingleProductDetail,
} from "data/api";
import { ProductDataType } from "data/types";
import DetailPagetLayout from "../Layout";
import SafeImage from "components/SafeImage/SafeImage";
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  PlayCircleIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";

const formatPdfName = (url: string) => {
  const filename = decodeURIComponent(url.split("/").pop() || "document");
  return filename
    .replace(/\.pdf$/i, "")
    .replace(/[_+-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const getYouTubeEmbedUrl = (url: string) => {
  const match = url.match(
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  );
  const videoId = match && match[2].length === 11 ? match[2] : null;
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

const ListingCarDetail: FC = () => {
  const { sku, id: categoryId } = useParams();
  const [data, setData] = useState<ProductDataType>({
    name: "",
    colors: "red",
    description: "",
    finishes: "",
    image_urls: [],
    SKU: String(sku),
    specifications: [],
    status: "",
    subcategory: { id: 0, name: "" },
    video_url: "",
    pdf_attachments: [],
  });
  const [colors, setColors] = useState<string[]>([]);
  const [finishes, setFinishes] = useState<string[]>([]);
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedFinish, setSelectedFinish] = useState("");
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const thisPathname = location.pathname;
  const router = useNavigate();

  useEffect(() => {
    getColorandFinish(sku!).then((items) => {
      setColors(items.data?.colors || []);
      setFinishes(items.data?.finishes || []);
    });
  }, [sku]);

  useEffect(() => {
    setLoading(true);
    getSingleProductDetail(sku!)
      .then((item) => {
        const product = item.data[0];
        setData(product);
        return getImages(product.SKU);
      })
      .then((resp) => setSelectedImages(resp?.data?.images || []))
      .finally(() => setLoading(false));
  }, [sku]);

  useEffect(() => {
    if (selectedColor) params.set("color", selectedColor);
    else params.delete("color");
    if (selectedFinish) params.set("finish", selectedFinish);
    else params.delete("finish");
    setParams(params, { replace: true });
  }, [selectedColor, selectedFinish]);

  useEffect(() => {
    const color = params.get("color");
    const finish = params.get("finish");
    if (!color) setSelectedColor("");
    if (!finish) setSelectedFinish("");
    if (!data.SKU) return;
    getImages(String(data.SKU), color || undefined, finish || undefined).then(
      (resp) => setSelectedImages(resp?.data?.images || [])
    );
  }, [location.search, data.SKU]);

  const openGallery = () => {
    params.set("modal", "PHOTO_TOUR_SCROLLABLE");
    params.set("photoId", "0");
    setParams(params);
    router(`${thisPathname}?${params.toString()}`, {
      state: { images: selectedImages },
      replace: true,
    });
  };

  const backHref = categoryId
    ? `/products/${categoryId}`
    : "/categories";

  const embedUrl = data.video_url ? getYouTubeEmbedUrl(data.video_url) : null;

  if (loading) {
    return (
      <div className="animate-pulse space-y-6 py-4">
        <div className="h-4 w-32 rounded bg-neutral-200 dark:bg-neutral-700" />
        <div className="grid md:grid-cols-4 md:grid-rows-2 gap-2 md:h-[420px]">
          <div className="md:col-span-2 md:row-span-2 rounded-2xl bg-neutral-200 dark:bg-neutral-700 min-h-[240px]" />
          <div className="hidden md:block rounded-2xl bg-neutral-200 dark:bg-neutral-700" />
          <div className="hidden md:block rounded-2xl bg-neutral-200 dark:bg-neutral-700" />
        </div>
        <div className="h-8 w-2/3 rounded bg-neutral-200 dark:bg-neutral-700" />
        <div className="h-4 w-full rounded bg-neutral-200 dark:bg-neutral-700" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{data.name} | BCR LTD. UK</title>
        <meta
          name="description"
          content={data.description?.slice(0, 160) || `View ${data.name} specifications and request a quote from BCR Ltd.`}
        />
      </Helmet>

      {/* Breadcrumb */}
      <Link
        to={backHref}
        className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-6"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to products
      </Link>

      {/* Gallery */}
      <div className="relative rounded-2xl overflow-hidden border border-neutral-200/80 dark:border-neutral-700/80 shadow-bcr-card">
        {selectedImages.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-1 md:gap-1.5 md:h-[460px] bg-neutral-100 dark:bg-neutral-800">
            <button
              type="button"
              onClick={openGallery}
              className="relative md:col-span-2 md:row-span-2 min-h-[260px] md:min-h-0 overflow-hidden group"
            >
              <img
                src={selectedImages[0]}
                alt={data.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </button>
            {selectedImages[1] && (
              <button
                type="button"
                onClick={openGallery}
                className="hidden md:block relative overflow-hidden group min-h-[140px]"
              >
                <img
                  src={selectedImages[1]}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </button>
            )}
            {selectedImages[2] && (
              <button
                type="button"
                onClick={openGallery}
                className="hidden md:block relative overflow-hidden group min-h-[140px]"
              >
                <img
                  src={selectedImages[2]}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {selectedImages.length > 3 && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-semibold text-lg">
                    +{selectedImages.length - 3} more
                  </div>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className="min-h-[260px] md:min-h-[360px] flex items-center justify-center bg-neutral-50 dark:bg-neutral-800">
            <SafeImage
              src={null}
              alt={data.name}
              placeholderVariant="product"
              className="w-full h-full min-h-[260px]"
              objectFit="contain"
            />
          </div>
        )}
        {selectedImages.length > 0 && (
          <button
            type="button"
            onClick={openGallery}
            className="absolute bottom-4 left-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 dark:bg-neutral-900/95 backdrop-blur-sm text-sm font-semibold text-neutral-900 dark:text-white shadow-bcr border border-neutral-200/80 dark:border-neutral-700 hover:shadow-bcr-lg transition-shadow"
          >
            <Squares2X2Icon className="w-4 h-4" />
            Show all photos
          </button>
        )}
      </div>

      {/* Main layout */}
      <div className="mt-8 lg:mt-10 grid lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_360px] gap-8 lg:gap-10 items-start">
        {/* Left column */}
        <div className="space-y-6 min-w-0">
          {/* Product header */}
          <div className="rounded-2xl border border-neutral-200/80 dark:border-neutral-700/80 bg-white dark:bg-neutral-900 p-6 sm:p-8 shadow-bcr-card">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                {data.subcategory?.name || "Product"}
              </span>
              {data.SKU && (
                <span className="inline-flex px-3 py-1 rounded-full text-[10px] font-semibold tracking-wide bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
                  SKU: {data.SKU}
                </span>
              )}
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1b3d] dark:text-white leading-tight tracking-tight">
              {data.name}
            </h1>

            {data.description && (
              <p className="mt-4 text-sm sm:text-base text-neutral-600 dark:text-neutral-300 leading-relaxed line-clamp-3">
                {data.description}
              </p>
            )}

            {/* Colour & finish */}
            {(colors.length > 0 || finishes.length > 0) && (
              <div className="mt-6 pt-6 border-t border-neutral-100 dark:border-neutral-800 space-y-5">
                {colors.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
                      Colour
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedColor("")}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                          !selectedColor
                            ? "bg-[#0f1b3d] text-white border-[#0f1b3d]"
                            : "bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700 hover:border-primary-400"
                        }`}
                      >
                        All
                      </button>
                      {colors.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setSelectedColor(c)}
                          className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                            selectedColor === c
                              ? "bg-[#0f1b3d] text-white border-[#0f1b3d]"
                              : "bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700 hover:border-primary-400"
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {finishes.length > 0 && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
                      Finish
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedFinish("")}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                          !selectedFinish
                            ? "bg-[#0f1b3d] text-white border-[#0f1b3d]"
                            : "bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700 hover:border-primary-400"
                        }`}
                      >
                        All
                      </button>
                      {finishes.map((f) => (
                        <button
                          key={f}
                          type="button"
                          onClick={() => setSelectedFinish(f)}
                          className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                            selectedFinish === f
                              ? "bg-[#0f1b3d] text-white border-[#0f1b3d]"
                              : "bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700 hover:border-primary-400"
                          }`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <Link
              to="/contact"
              state={{ sku: data.SKU, name: data.name }}
              className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bcr-btn-primary h-12 px-8 rounded-2xl text-sm font-semibold"
            >
              Request a quote
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>

          {/* Specifications */}
          {data.specifications?.length > 0 && (
            <div className="rounded-2xl border border-neutral-200/80 dark:border-neutral-700/80 bg-white dark:bg-neutral-900 p-6 sm:p-8 shadow-bcr-card">
              <span className="text-xs font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400">
                Technical details
              </span>
              <h2 className="mt-2 text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
                Product specifications
              </h2>
              <div className="mt-6 space-y-4">
                {data.specifications.map((item, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-800/30 p-5"
                  >
                    <div className="grid sm:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          Code
                        </p>
                        <p className="mt-1 font-mono font-semibold text-neutral-900 dark:text-white">
                          {item.code}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                          Material
                        </p>
                        <p className="mt-1 text-neutral-700 dark:text-neutral-300">
                          {item.material}
                        </p>
                      </div>
                    </div>
                    {item.dimension?.dimensions?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-neutral-200/80 dark:border-neutral-700/80">
                        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">
                          Dimensions
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {item.dimension.dimensions.map((dim: any, di: number) =>
                            Object.entries(dim).map(([key, val]) => (
                              <span
                                key={`${di}-${key}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 text-sm"
                              >
                                <span className="font-semibold text-neutral-800 dark:text-neutral-200 capitalize">
                                  {key}:
                                </span>
                                <span className="text-neutral-600 dark:text-neutral-400">
                                  {String(val)}
                                </span>
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Full description */}
          {data.description && (
            <div className="rounded-2xl border border-neutral-200/80 dark:border-neutral-700/80 bg-white dark:bg-neutral-900 p-6 sm:p-8 shadow-bcr-card">
              <span className="text-xs font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400">
                Overview
              </span>
              <h2 className="mt-2 text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
                Product description
              </h2>
              <p className="mt-4 text-sm sm:text-base text-neutral-600 dark:text-neutral-300 leading-relaxed whitespace-pre-line">
                {data.description}
              </p>
            </div>
          )}

          {/* Video */}
          {embedUrl && (
            <div className="rounded-2xl border border-neutral-200/80 dark:border-neutral-700/80 bg-white dark:bg-neutral-900 p-6 sm:p-8 shadow-bcr-card">
              <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400">
                <PlayCircleIcon className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-wider">
                  Product video
                </span>
              </div>
              <div className="mt-4 relative rounded-xl overflow-hidden aspect-video bg-neutral-900">
                <iframe
                  src={embedUrl}
                  title="Product video"
                  className="absolute inset-0 w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 space-y-4">
          <div className="rounded-2xl overflow-hidden border border-neutral-200/80 dark:border-neutral-700/80 bg-white dark:bg-neutral-900 shadow-bcr-card">
            <div className="bg-gradient-to-br from-[#0f1b3d] to-[#1a3266] px-6 py-5">
              <h3 className="text-lg font-bold text-white">Get a quote</h3>
              <p className="mt-1 text-sm text-white/70">
                Speak to our team about pricing, availability &amp; custom specs.
              </p>
            </div>
            <div className="p-6">
              <Link
                to="/contact"
                state={{ sku: data.SKU, name: data.name }}
                className="w-full inline-flex items-center justify-center gap-2 bcr-btn-primary h-12 rounded-2xl text-sm font-semibold"
              >
                Request a quote
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
              <p className="mt-4 text-xs text-center text-neutral-500 dark:text-neutral-400">
                UK-quality profiles · UAE &amp; GCC delivery
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-neutral-200/80 dark:border-neutral-700/80 bg-white dark:bg-neutral-900 shadow-bcr-card overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-100 dark:border-neutral-800 flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-red-600" />
              <h3 className="font-bold text-neutral-900 dark:text-white">
                Technical documents
              </h3>
            </div>
            <div className="p-4 space-y-2">
              {data.pdf_attachments.length === 0 ? (
                <p className="text-sm text-neutral-500 text-center py-4">
                  No documents available
                </p>
              ) : (
                data.pdf_attachments.map((pdfUrl, i) => (
                  <a
                    key={i}
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="flex items-center gap-3 w-full p-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 hover:border-primary-300 dark:hover:border-primary-600 hover:bg-primary-50/50 dark:hover:bg-primary-900/20 transition-all group"
                  >
                    <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                      <ArrowDownTrayIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </span>
                    <span className="flex-1 min-w-0 text-sm font-medium text-neutral-800 dark:text-neutral-200 truncate group-hover:text-primary-700 dark:group-hover:text-primary-300">
                      {formatPdfName(pdfUrl)}
                    </span>
                    <span className="text-[10px] font-bold uppercase text-red-600 bg-red-50 dark:bg-red-900/30 px-2 py-0.5 rounded">
                      PDF
                    </span>
                  </a>
                ))
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Mobile sticky CTA */}
      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden p-4 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-t border-neutral-200 dark:border-neutral-700">
        <Link
          to="/contact"
          state={{ sku: data.SKU, name: data.name }}
          className="w-full inline-flex items-center justify-center gap-2 bcr-btn-primary h-12 rounded-2xl text-sm font-semibold"
        >
          Request a quote
          <ArrowRightIcon className="w-4 h-4" />
        </Link>
      </div>
    </>
  );
};

const ProductDetails = () => (
  <DetailPagetLayout>
    <ListingCarDetail />
  </DetailPagetLayout>
);

export default ProductDetails;
